'use client'

import { Button } from '@/components/ui/button'
import { getArenaUrl } from '@/lib/arena'
import { useI18n } from '@/lib/i18n'
import { withLanguagePath } from '@/lib/url-language-utils'
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  History as HistoryIcon,
  RefreshCw,
  Target,
} from 'lucide-react'
import { useEffect, useState } from 'react'

function renderTextWithBold(text: string) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g)
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i}>
          {part.slice(2, -2)}
        </strong>
      )
    }
    return part
  })
}

export function OurVisionSection() {
  const [isMounted, setIsMounted] = useState(false)
  const { language, t } = useI18n()
  const arenaOrigin = getArenaUrl()
  const gameHref = arenaOrigin
    ? `${arenaOrigin}${withLanguagePath('/new', language)}`
    : withLanguagePath('/new', language)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    if (!isMounted)
      return

    const equalizeCardHeights = () => {
      const problemCards = Array.from(document.querySelectorAll('.problem-card')) as HTMLElement[]
      const solutionCards = Array.from(document.querySelectorAll('.solution-card')) as HTMLElement[]

      if (problemCards.length === 0 || solutionCards.length === 0)
        return

      const allCards = [...problemCards, ...solutionCards]
      allCards.forEach((card) => {
        card.style.minHeight = 'auto'
      })

      const isTwoColumnLayout
        = typeof window !== 'undefined'
          && window.matchMedia('(min-width: 640px)').matches

      if (!isTwoColumnLayout)
        return

      for (const [i, leftCard] of problemCards.entries()) {
        const rightCard = solutionCards[i]

        if (!leftCard || !rightCard)
          continue

        const pairMaxHeight = Math.max(
          leftCard.offsetHeight,
          rightCard.offsetHeight,
        )

        leftCard.style.minHeight = `${pairMaxHeight}px`
        rightCard.style.minHeight = `${pairMaxHeight}px`
      }
    }

    const timeoutId = setTimeout(equalizeCardHeights, 100)

    let resizeTimeoutId: NodeJS.Timeout
    const handleResize = () => {
      clearTimeout(resizeTimeoutId)
      resizeTimeoutId = setTimeout(equalizeCardHeights, 100)
    }
    window.addEventListener('resize', handleResize)

    return () => {
      clearTimeout(timeoutId)
      clearTimeout(resizeTimeoutId)
      window.removeEventListener('resize', handleResize)
    }
  }, [isMounted, t])

  const problems = [
    {
      icon: AlertTriangle,
      title: t('whyMentiss.problem1Title'),
      description: t('whyMentiss.problem1Desc'),
    },
    {
      icon: AlertTriangle,
      title: t('whyMentiss.problem2Title'),
      description: t('whyMentiss.problem2Desc'),
    },
    {
      icon: AlertTriangle,
      title: t('whyMentiss.problem3Title'),
      description: t('whyMentiss.problem3Desc'),
    },
    {
      icon: AlertTriangle,
      title: t('whyMentiss.problem4Title'),
      description: t('whyMentiss.problem4Desc'),
    },
  ]

  const solutions = [
    {
      icon: CheckCircle2,
      title: t('whyMentiss.solution1Title'),
      description: t('whyMentiss.solution1Desc'),
    },
    {
      icon: CheckCircle2,
      title: t('whyMentiss.solution2Title'),
      description: t('whyMentiss.solution2Desc'),
    },
    {
      icon: CheckCircle2,
      title: t('whyMentiss.solution3Title'),
      description: t('whyMentiss.solution3Desc'),
    },
    {
      icon: CheckCircle2,
      title: t('whyMentiss.solution4Title'),
      description: t('whyMentiss.solution4Desc'),
    },
  ]

  return (
    <div className="min-h-dvh overflow-x-clip bg-section-light px-4 py-20 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">

        { }
        <div className="mb-16 text-center">
          <h2 className="mb-4 text-3xl font-bold text-foreground md:text-4xl">
            {t('ourVision.cta.start.label')}
          </h2>
          {t('whyMentiss.subtitleHeader') && (
            <p className="mx-auto mb-8 max-w-4xl text-lg text-muted-foreground">
              {renderTextWithBold(t('whyMentiss.subtitleHeader'))}
            </p>
          )}

          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Button
              asChild
              size="lg"
              className="group relative inline-flex items-center gap-2 overflow-hidden rounded-2xl border-2 border-slate-400/25 bg-gradient-to-br from-slate-800 via-slate-900 to-black p-6 text-sm font-medium text-slate-100 shadow-lg shadow-black/60 backdrop-blur-md transition-all before:absolute before:inset-0 before:bg-gradient-to-br before:from-white/15 before:to-transparent before:opacity-50 before:transition-opacity after:absolute after:inset-0 after:bg-gradient-to-tl after:via-transparent after:to-white/10 after:opacity-40 after:transition-opacity hover:border-slate-300/40 hover:from-slate-700 hover:via-slate-800 hover:to-slate-900 hover:shadow-xl hover:shadow-slate-800/70 hover:before:opacity-70 hover:after:opacity-65 dark:border-white/40 dark:from-white/25 dark:via-zinc-100/30 dark:to-white/35 dark:text-slate-900 dark:shadow-white/20 dark:backdrop-blur-xl dark:before:from-white/50 dark:before:via-white/30 dark:before:opacity-70 dark:after:from-white/40 dark:after:to-white/30 dark:after:opacity-60 dark:hover:border-white/60 dark:hover:from-white/35 dark:hover:via-zinc-50/40 dark:hover:to-white/45 dark:hover:shadow-white/30 dark:hover:before:opacity-90 dark:hover:after:opacity-85 sm:px-8 sm:py-7 sm:text-base"
            >
              <a href={`${gameHref}?tab=byom`}>
                <span className="relative z-10 inline-flex items-center gap-2">
                  {t('ourVision.cta.contactUs')}
                  <ArrowRight className="size-4 sm:size-5" />
                </span>
              </a>
            </Button>
          </div>
        </div>

        { }
        <div className="mb-24 flex flex-col items-center">
          <div className="relative mb-12 flex w-full max-w-5xl flex-col gap-2 lg:flex-row lg:items-stretch">
            { }
            <div className="absolute -left-20 -top-20 size-64 rounded-full bg-primary/5 blur-[100px]" />
            <div className="absolute -bottom-20 -right-20 size-64 rounded-full bg-primary/5 blur-[100px]" />

            { }
            <div
              className="group relative z-10 flex flex-1 flex-col justify-center rounded-3xl border border-primary/20 bg-gradient-to-br from-primary/5 via-background/40 to-transparent p-5 shadow-sm backdrop-blur-md transition-all duration-300 hover:border-primary/40 hover:bg-background/60 lg:p-6"
            >
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex size-11 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/20 transition-all group-hover:scale-110 group-hover:bg-primary group-hover:text-white group-hover:ring-primary">
                    <Target className="size-5" />
                  </div>
                  <h4 className="text-lg font-bold text-foreground">
                    {t('ourVision.cta.start.label')}
                  </h4>
                </div>
              </div>

              <div className="grid gap-3">
                <div className="flex items-center gap-4 rounded-xl border border-primary/10 bg-primary/5 p-3 transition-colors group-hover:border-primary/20">
                  <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary group-hover:bg-primary/20">1</div>
                  <span className="text-sm font-medium text-muted-foreground">{renderTextWithBold(t('ourVision.cta.start.step1'))}</span>
                </div>
                <div className="flex items-center gap-4 rounded-xl border border-primary/10 bg-primary/5 p-3 transition-colors group-hover:border-primary/20">
                  <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary group-hover:bg-primary/20">2</div>
                  <span className="text-sm font-medium text-muted-foreground">{renderTextWithBold(t('ourVision.cta.start.step2'))}</span>
                </div>
              </div>
            </div>

            { }
            <div className="flex items-center justify-center lg:px-4">
              <div className="relative flex items-center justify-center">
                { }
                <div className="duration-[3000ms] absolute size-16 animate-ping rounded-full bg-primary/10" />
                <div className="absolute size-12 animate-pulse rounded-full bg-primary/20" />

                { }
                <div className="relative z-10 flex size-12 items-center justify-center rounded-2xl border border-primary/30 bg-background/80 shadow-2xl ring-1 ring-primary/20 backdrop-blur-xl">
                  { }
                  <ArrowRight className="size-6 rotate-90 text-primary transition-transform duration-500 lg:rotate-0" />

                  { }
                  <div className="absolute -left-12 -z-10 hidden w-12 items-center justify-center lg:flex">
                    <div className="h-px w-full bg-gradient-to-r from-transparent via-primary/40 to-primary/60" />
                    <div className="absolute right-0 size-1 animate-[move-right_2s_linear_infinite] rounded-full bg-primary" />
                  </div>
                  <div className="absolute -right-12 -z-10 hidden w-12 items-center justify-center lg:flex">
                    <div className="h-px w-full bg-gradient-to-r from-primary/60 via-primary/40 to-transparent" />
                    <div className="absolute left-0 size-1 animate-[move-right_2s_linear_infinite] rounded-full bg-primary" />
                  </div>

                  { }
                  <div className="absolute -top-12 -z-10 flex h-12 flex-col items-center justify-center lg:hidden">
                    <div className="h-full w-px bg-gradient-to-b from-transparent via-primary/40 to-primary/60" />
                    <div className="absolute top-0 size-1 animate-[move-down_2s_linear_infinite] rounded-full bg-primary" />
                  </div>
                  <div className="absolute -bottom-12 -z-10 flex h-12 flex-col items-center justify-center lg:hidden">
                    <div className="h-full w-px bg-gradient-to-b from-primary/60 via-primary/40 to-transparent" />
                    <div className="absolute top-0 size-1 animate-[move-down_2s_linear_infinite] rounded-full bg-primary" />
                  </div>
                </div>
              </div>
            </div>

            { }
            <div className="flex flex-1 flex-col gap-4">

              { }
              <div className="group relative flex flex-1 items-center gap-5 rounded-2xl border border-green-500/20 bg-gradient-to-r from-green-500/10 to-transparent p-5 shadow-sm transition-all duration-300 hover:border-green-500/40 hover:bg-green-500/20 hover:shadow-lg">
                <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-green-500/20 text-green-600 ring-1 ring-green-500/30 transition-all group-hover:scale-110 group-hover:bg-green-500 group-hover:text-white dark:text-green-400">
                  <Target className="size-5" />
                </div>
                <div className="flex-1">
                  <h5 className="flex items-center gap-2 text-lg font-bold text-foreground">
                    {t('ourVision.cta.pathSuccess.result')}
                    <CheckCircle2 className="size-5 text-green-500" />
                  </h5>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                    {renderTextWithBold(t('ourVision.cta.pathSuccess.step3'))}
                  </p>
                </div>
              </div>

              { }
              <div
                className="group relative flex flex-1 items-center gap-5 rounded-2xl border border-amber-500/20 bg-gradient-to-r from-amber-500/10 to-transparent p-5 shadow-sm transition-all duration-300 hover:border-amber-500/40 hover:bg-amber-500/20 hover:shadow-lg"
              >
                <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-amber-500/20 text-amber-600 ring-1 ring-amber-500/30 transition-all group-hover:scale-110 group-hover:bg-amber-500 group-hover:text-white dark:text-amber-400">
                  <RefreshCw className="size-5" />
                </div>
                <div className="flex-1">
                  <h5 className="flex items-center gap-2 text-lg font-bold text-foreground">
                    {t('ourVision.cta.pathIterate.loop')}
                    <HistoryIcon className="size-5 text-amber-500" />
                  </h5>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                    {renderTextWithBold(t('ourVision.cta.pathIterate.step4'))}
                  </p>
                </div>
              </div>

            </div>
          </div>

        </div>

        { }
        <div className="my-16 flex items-center justify-center">
          <div className="flex w-full max-w-5xl items-center">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-300 to-transparent dark:via-slate-600" />
            <div className="mx-6 flex-shrink-0">
              <h3 className="text-lg font-semibold text-slate-600 dark:text-slate-400">
                {t('whyMentiss.challengesSolutions')}
              </h3>
            </div>
            <div className="h-px flex-1 bg-gradient-to-l from-transparent via-slate-300 to-transparent dark:via-slate-600" />
          </div>
        </div>

        { }
        <div className="mx-auto mb-16 max-w-5xl">
          <div className="grid grid-cols-1 gap-12 sm:grid-cols-2 sm:gap-6">
            { }
            <div id="problem" className="flex scroll-mt-20 flex-col gap-6">
              <div className="flex items-center justify-center gap-3 pb-2">
                <AlertTriangle className="size-6 text-red-600 dark:text-red-400" />
                <h2 className="text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                  {t('whyMentiss.problemHeader')}
                </h2>
              </div>
              {problems.map((problem, index) => {
                return (
                  <div
                    key={`problem-${index}`}
                    className="problem-card flex h-full flex-col gap-3 rounded-lg border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800"
                  >
                    <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                      {problem.title}
                    </h3>
                    <p className="leading-relaxed text-slate-700 dark:text-slate-300">
                      {problem.description}
                    </p>
                  </div>
                )
              })}
            </div>

            { }
            <div id="solutions" className="flex scroll-mt-20 flex-col gap-6">
              <div className="flex items-center justify-center gap-3 pb-2">
                <CheckCircle2 className="size-6 text-green-600 dark:text-green-400" />
                <h2 className="text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                  {t('whyMentiss.solutionHeader')}
                </h2>
              </div>
              {solutions.map((solution, index) => {
                return (
                  <div
                    key={`solution-${index}`}
                    className="solution-card flex h-full flex-col gap-3 rounded-lg border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800"
                  >
                    <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                      {solution.title}
                    </h3>
                    <p className="leading-relaxed text-slate-700 dark:text-slate-300">
                      {solution.description}
                    </p>
                  </div>
                )
              })}
            </div>
          </div>
        </div>

      </div>
    </div>
  )
}
